import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import NewsCard from "@/components/feature/NewsCard";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Search() {
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();

  useDocumentMeta(
    q ? `“${q}” 搜索结果 - 51动漫` : "搜索 - 51动漫",
    q
      ? `51动漫关于“${q}”的搜索结果，涵盖相关动漫作品与资讯内容。`
      : "在 51动漫搜索动漫作品与资讯内容。"
  );

  const { animeResults, newsResults } = useMemo(() => {
    if (!q) return { animeResults: [], newsResults: [] };
    const kw = q.toLowerCase();
    const anime = animeList.filter(
      (a) =>
        a.title.toLowerCase().includes(kw) ||
        a.summary.toLowerCase().includes(kw) ||
        a.synopsis.toLowerCase().includes(kw) ||
        a.tags.some((t) => t.toLowerCase().includes(kw))
    );
    const news = newsList.filter(
      (n) =>
        n.title.toLowerCase().includes(kw) ||
        n.summary.toLowerCase().includes(kw) ||
        n.content.some((c) => c.toLowerCase().includes(kw))
    );
    return { animeResults: anime, newsResults: news };
  }, [q]);

  const total = animeResults.length + newsResults.length;

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-5 mb-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {q ? `“${q}” 的搜索结果` : "全站搜索"}
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500">
          {q ? `共找到 ${total} 条相关内容（动漫 ${animeResults.length} 部，资讯 ${newsResults.length} 篇）` : "请输入关键词，搜索动漫作品与资讯内容。"}
        </p>
      </div>

      {!q && (
        <div className="py-16 flex flex-col items-center justify-center text-center rounded-lg bg-background-100 border border-background-200">
          <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mb-4">
            <i className="ri-search-line text-3xl"></i>
          </span>
          <p className="text-sm text-foreground-500">使用顶部搜索框输入关键词开始搜索。</p>
        </div>
      )}

      {q && total === 0 && (
        <div className="py-16 md:py-24 flex flex-col items-center justify-center text-center rounded-lg bg-background-100 border border-background-200">
          <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">
            没有找到与“{q}”相关的内容
          </h3>
          <p className="text-sm text-foreground-500 mb-5 max-w-sm">
            试试更换关键词，或浏览动漫列表发现更多作品。
          </p>
          <Link
            to="/anime"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-list-check"></i>
            浏览全部动漫
          </Link>
        </div>
      )}

      {q && animeResults.length > 0 && (
        <section className="mb-12">
          <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-movie-line text-primary-500"></i>
            相关动漫
            <span className="text-sm font-normal text-foreground-500">（{animeResults.length}）</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {animeResults.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
        </section>
      )}

      {q && newsResults.length > 0 && (
        <section>
          <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-newspaper-line text-primary-500"></i>
            相关资讯
            <span className="text-sm font-normal text-foreground-500">（{newsResults.length}）</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {newsResults.map((news) => (
              <NewsCard key={news.id} news={news} layout="vertical" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}